import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Wallet, Plus, Trash, MagnifyingGlass, X
} from '@phosphor-icons/react';

const CATEGORIES = ['Food', 'Transport', 'Shopping', 'Entertainment', 'Health', 'Education', 'Subscriptions', 'Other'];
const CAT_COLORS = { Food: '#FF6B35', Transport: '#3B82F6', Shopping: '#EC4899', Entertainment: '#A855F7', Health: '#22C55E', Education: '#F59E0B', Subscriptions: '#00E5FF', Other: '#737373' };

export default function PersonalExpensesPage() {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [members, setMembers] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedMember, setSelectedMember] = useState('');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [form, setForm] = useState({ member_id: '', amount: '', category: 'Other', description: '', date: new Date().toISOString().slice(0, 10) });

  const fetchData = async () => {
    try {
      const [memRes, sumRes] = await Promise.all([
        api.get('/members'),
        api.get('/personal-expenses/summary', { params: { month } })
      ]);
      setMembers(memRes.data);
      setSummary(sumRes.data);
      const expParams = { month };
      if (selectedMember) expParams.member_id = selectedMember;
      const expRes = await api.get('/personal-expenses', { params: expParams });
      setExpenses(expRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [api, month, selectedMember]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/personal-expenses', { ...form, amount: Number(form.amount) });
      setShowForm(false); setForm({ member_id: '', amount: '', category: 'Other', description: '', date: new Date().toISOString().slice(0, 10) }); fetchData();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => { try { await api.delete(`/personal-expenses/${id}`); fetchData(); } catch (e) { console.error(e); } };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="personal-expenses-page" className="space-y-6 relative">
      <div className="glow-orb-teal -top-20 right-0 z-0" />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 relative z-10">
        <div>
          <p className="overline">Finance</p>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight mt-1">{t('personal_expenses')}</h1>
          <p className="text-sm text-neutral-500 mt-1">Track per-member spending and allowances</p>
        </div>
        <div className="flex items-center gap-3">
          <input type="month" value={month} onChange={e => setMonth(e.target.value)} data-testid="pe-month-filter"
            className="px-4 py-2.5 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
          <button onClick={() => setShowForm(true)} data-testid="add-personal-expense-btn" className="px-5 py-2.5 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow flex items-center gap-2"><Plus size={16} /> {t('add_expense')}</button>
        </div>
      </div>

      {/* Member Summary Cards */}
      {summary && summary.members && summary.members.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative z-10">
          {summary.members.map(m => {
            const pct = m.percentage;
            const isOver = pct > 100;
            return (
              <div key={m.member_id}
                className={`glass-card p-5 cursor-pointer transition-all duration-200 animate-fade-up ${selectedMember === m.member_id ? 'border-[#FF6B35]/40 ring-1 ring-[#FF6B35]/20' : ''}`}
                onClick={() => setSelectedMember(selectedMember === m.member_id ? '' : m.member_id)}
                data-testid={`pe-summary-${m.member_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-white">{m.name}</p>
                  {m.allowance > 0 && (
                    <span className={`text-xs font-semibold ${isOver ? 'text-red-400' : pct >= 80 ? 'text-amber-400' : 'text-emerald-400'}`}>{pct}%</span>
                  )}
                </div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-2xl font-heading font-bold text-white">Rs.{m.spent.toLocaleString('en-IN')}</span>
                  {m.allowance > 0 && <span className="text-xs text-neutral-500">/ Rs.{m.allowance.toLocaleString('en-IN')}</span>}
                </div>
                {m.allowance > 0 && (
                  <div className="w-full h-1.5 rounded-full bg-white/5 overflow-hidden mb-2">
                    <div className={`h-full rounded-full ${isOver ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                )}
                {Object.keys(m.by_category).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {Object.entries(m.by_category).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([cat, amt]) => (
                      <span key={cat} className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${CAT_COLORS[cat] || '#737373'}15`, color: CAT_COLORS[cat] || '#737373' }}>
                        {cat}: Rs.{amt.toLocaleString('en-IN')}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Expense List */}
      <div className="glass-card p-6 relative z-10" data-testid="pe-expense-list">
        <h2 className="font-heading font-semibold text-white text-lg mb-4">
          {selectedMember ? `${members.find(m => m.member_id === selectedMember)?.name || ''}'s Expenses` : 'All Expenses'}
        </h2>
        {expenses.length === 0 ? (
          <div className="text-center py-10"><Wallet size={36} weight="duotone" className="mx-auto text-neutral-600 mb-3" /><p className="text-neutral-500 text-sm">No expenses for this period</p></div>
        ) : (
          <div className="space-y-2">
            {expenses.map(exp => {
              const catColor = CAT_COLORS[exp.category] || '#737373';
              return (
                <div key={exp.expense_id} className="flex items-center justify-between bg-white/[0.02] rounded-xl px-4 py-3" data-testid={`pe-expense-${exp.expense_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${catColor}15` }}>
                      <Wallet size={16} style={{ color: catColor }} />
                    </div>
                    <div>
                      <p className="text-sm text-white">{exp.description || exp.category}</p>
                      <p className="text-[11px] text-neutral-500">{exp.member_name} &middot; {exp.date} &middot; <span style={{ color: catColor }}>{exp.category}</span></p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-white">Rs.{exp.amount.toLocaleString('en-IN')}</span>
                    <button onClick={() => handleDelete(exp.expense_id)} className="p-1 rounded-lg hover:bg-white/5 text-neutral-600 hover:text-red-400"><Trash size={14} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-[#141414]/95 backdrop-blur-3xl border border-white/10 rounded-[24px] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-semibold text-white text-lg mb-5">{t('add_expense')}</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div><label className="overline block mb-2">Member</label><select value={form.member_id} onChange={e => setForm({...form, member_id: e.target.value})} required data-testid="pe-member-select" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50"><option value="">Select member</option>{members.map(m => <option key={m.member_id} value={m.member_id}>{m.name}</option>)}</select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline block mb-2">{t('amount')} (Rs.)</label><input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required data-testid="pe-amount" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /></div>
                <div><label className="overline block mb-2">{t('category')}</label><select value={form.category} onChange={e => setForm({...form, category: e.target.value})} data-testid="pe-category" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50">{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
              </div>
              <div><label className="overline block mb-2">Description</label><input type="text" value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="pe-desc" placeholder="e.g., School books" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /></div>
              <div><label className="overline block mb-2">{t('date')}</label><input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="pe-date" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /></div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-3 rounded-full border border-white/10 text-neutral-400 text-sm font-medium hover:bg-white/5">{t('cancel')}</button>
                <button type="submit" data-testid="save-pe-btn" className="flex-1 py-3 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow">{t('save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
